import { useState, useEffect } from "react";
import { View } from "tamagui";
import { useNavigate, useLocation } from "react-router-dom";

import { colors } from "@styles/colors";

import CardList from "./components/paymentMethod/CardList";
import CardForm from "./components/paymentMethod/CardForm";
import Processing from "./components/paymentMethod/Processing";
import { handleAPIRegisterPaymentMethod, handleAPIListPaymentMethods, type OwnedPaymentMethod, type ReturnContext } from "./API/handlePaymentAPI";
import { shouldRedirectToChallenge } from "@utils/bindChallengeRedirect";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { buildXContextHeader } from "@utils/api/headers";
import type { IdentityContext } from "@utils/store/user/useUser";
import appToast from "@components/Toast";

/** 由帳戶付款頁帶入的綁卡意圖（§七 兩路徑）；無 state = 純個人錢包綁卡。 */
interface BindIntentState {
  context?: IdentityContext;
  bindScope?: "personal" | "context";
  shareWithContext?: boolean;
  returnTo?: string;
  /** 3DS challenge 完成後導回意圖（§6）；未帶則落地頁無法自動導回。 */
  returnContext?: ReturnContext;
}

/** API 已綁付款方式 → CardList 期望的卡片欄位 */
const mapMethodToCard = (m: OwnedPaymentMethod) => ({
  id: m.payment_method_id,
  last4: m.card?.lastfour ?? "",
  digits: m.card?.bincode ?? "4",
  holder: m.card?.issuer ?? "",
  expiry: m.card?.expiry_month ? `${String(m.card.expiry_month).padStart(2, "0")}/${String(m.card.expiry_year ?? "").slice(-2)}` : "",
});

export const PaymentMethod = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cusJwtToken } = useCusAuth();

  // 綁卡意圖（從帳戶付款頁帶入）：scope=context → 帳戶卡；shareWithContext → 個人卡並授權此帳戶
  const intent = (location.state as BindIntentState | null) ?? {};
  const bindScope = intent.bindScope ?? "personal";
  const xContext = buildXContextHeader(intent.context);

  // screen: list | form | processing_auth | processing_save
  // 帶綁卡意圖進來時直接進表單，省一層列表
  const [screen, setScreen] = useState(intent.bindScope ? "form" : "list");
  const [cards, setCards] = useState<any[]>([]);

  //-- 當切換頁面時，觸發全局捲動至最上方事件
  useEffect(() => {
    const triggerScroll = () => {
      window.dispatchEvent(new Event("scroll-to-top"));
    };
    triggerScroll();
    const timer = setTimeout(triggerScroll, 50);
    return () => clearTimeout(timer);
  }, [screen]);

  // 載入 caller 已綁定的信用卡
  useEffect(() => {
    if (!cusJwtToken) return;
    let active = true;
    handleAPIListPaymentMethods(cusJwtToken).then((r) => {
      if (!active) return;
      if (r.state === "success") {
        setCards(r.items.filter((m) => m.method_type === "credit_card").map(mapMethodToCard));
      }
    });
    return () => {
      active = false;
    };
  }, [cusJwtToken]);

  const goForm = () => setScreen("form");
  const goList = () => setScreen("list");
  // 帶意圖進來時，表單返回應回帳戶付款頁而非個人錢包列表
  const backFromForm = () => {
    if (intent.returnTo) {
      navigate(intent.returnTo, { state: { context: intent.context } });
    } else {
      goList();
    }
  };

  const afterForm = async (info: any) => {
    setScreen("processing_save");

    if (cusJwtToken && info) {
      try {
        const result = await handleAPIRegisterPaymentMethod(
          {
            method_type: "credit_card",
            prime: info?.card?.prime,
            nickname: `**** **** **** ${info?.card?.lastfour}` || "卡片",
            scope: bindScope,
            share_with_context: intent.shareWithContext === true,
            return_context: intent.returnContext ?? { type: "wallet" },
          },
          cusJwtToken,
          // scope=context 或 share 都需顯式 X-Context
          bindScope === "context" || intent.shareWithContext ? xContext : undefined,
        );

        if (result.state === "success") {
          const challengeUrl = shouldRedirectToChallenge(result.data);
          if (challengeUrl) {
            // 銀行要求 3DS challenge：跳出 LIFF 到銀行驗證頁，完成後由落地頁依 return_context 導回。
            // bind_ref 存 localStorage（不放進 frontend_redirect_url 的 query，否則 TapPay
            // 不會送 backend_notify）；落地頁導回後從 localStorage 讀取來輪詢 bind-result。
            if (result.data.bind_status === "challenge_required" && result.data.bind_ref) {
              localStorage.setItem("bind_3ds_ref", result.data.bind_ref);
            }
            window.location.href = challengeUrl;
            return;
          }
          // 從帳戶頁來的綁卡 → 綁完回帳戶付款頁刷新清單
          if (intent.returnTo) {
            navigate(intent.returnTo, { state: { context: intent.context } });
          } else {
            navigate(`/account/paymentMethod/result?status=success&lastfour=${info?.card?.lastfour}&type=${info?.card?.type}`);
          }
        } else {
          const errorMsg = Array.isArray(result.error) ? result.error.join("\n") : result.error;
          navigate(`/account/paymentMethod/result?status=error&msg=${encodeURIComponent(errorMsg)}`);
        }
      } catch (e) {
        navigate(`/account/paymentMethod/result?status=error&msg=${encodeURIComponent("系統發生錯誤，請稍後再試")}`);
      }
    } else {
      appToast.error("取得授權錯誤");
      setTimeout(() => {
        navigate("/account");
      }, 1500);
    }
  };

  return (
    <View flex={1} backgroundColor={colors.bg} overflow="hidden">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes popIn {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.12); }
          100% { transform: scale(1); opacity: 1; }
        }
        *::-webkit-scrollbar { display: none; }
      `}</style>

      {screen === "list" && (
        <CardList
          cards={cards}
          onAdd={goForm}
          onBack={() => navigate("/account")}
          onCardPress={(card) => navigate(`/account/paymentMethod/detail/${card?.id ?? ""}`)}
        />
      )}
      {screen === "form" && <CardForm onNext={afterForm} onBack={backFromForm} />}
      {(screen === "processing_auth" || screen === "processing_save") && (
        <Processing message={screen === "processing_auth" ? "透過 TapPay 安全加密通道\n驗證你的信用卡資訊" : "完成驗證\n正在安全儲存 TapPay Token"} />
      )}
    </View>
  );
};

export default PaymentMethod;
